import React, { useEffect, useState } from "react";
import { CreditCard, Plus, Pencil, Power } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, Loading, PageHeader, Button, Modal, Badge } from "@/components/common";

const TYPES = [["cash", "Tunai"], ["transfer", "Transfer"], ["edc", "EDC/Kartu"], ["qris", "QRIS"], ["receivable", "Piutang"], ["online", "Online"], ["other", "Lainnya"]];

export default function PaymentMethods() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setList(await api.get("/payment-methods", { active_only: false })); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (m) => {
    try { await api.patch(`/payment-methods/${m.id}`, { is_active: !m.is_active }); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (loading) return <Loading />;
  return (
    <>
      <PageHeader title="Metode Pembayaran" subtitle={`${list.length} metode`} icon={CreditCard}
        actions={<Button onClick={() => setEditing({})} data-testid="add-method-btn"><Plus size={16} /> Metode Baru</Button>} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((m) => (
          <Card key={m.id} className="p-4 flex items-center justify-between" data-testid={`method-${m.id}`}>
            <div>
              <div className="font-semibold text-slate-800">{m.name}</div>
              <Badge className="bg-slate-100 text-slate-600 mt-1">{TYPES.find((t) => t[0] === m.type)?.[1] || m.type}</Badge>
              {!m.is_active && <Badge className="bg-rose-100 text-rose-700 ml-1">Nonaktif</Badge>}
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEditing(m)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><Pencil size={15} /></button>
              <button onClick={() => toggle(m)} className={`w-8 h-8 rounded-lg flex items-center justify-center ${m.is_active ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`}><Power size={15} /></button>
            </div>
          </Card>
        ))}
      </div>
      {editing && <MethodModal method={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function MethodModal({ method, onClose, onSaved }) {
  const isNew = !method.id;
  const [name, setName] = useState(method.name || "");
  const [type, setType] = useState(method.type || "cash");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("Nama wajib diisi");
    setBusy(true);
    try { if (isNew) await api.post("/payment-methods", { name, type }); else await api.patch(`/payment-methods/${method.id}`, { name, type }); toast.success("Tersimpan"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={isNew ? "Metode Baru" : "Edit Metode"} maxWidth="max-w-sm">
      <div className="space-y-3">
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Nama</span><input value={name} onChange={(e) => setName(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="mf-pm-name" /></label>
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Tipe</span><select value={type} onChange={(e) => setType(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300">{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        <Button className="w-full" onClick={save} disabled={busy} data-testid="mf-pm-save">{busy ? "Menyimpan..." : "Simpan"}</Button>
      </div>
    </Modal>
  );
}
